/**
 * 编组缩放 Hook
 *
 * 与 useResize 逻辑相同，但额外处理子元素按比例缩放：
 *  - 拖拽中：updateElementSilent 分别更新组容器和每个子元素（实时预览，不生成历史）
 *  - 松开：commitGroupResize 原子地提交所有变更（单条历史记录）
 *
 * 子元素缩放规则（以拖拽开始时的快照为基准）：
 *  childX'     = childX_orig  * scaleX
 *  childY'     = childY_orig  * scaleY
 *  childWidth' = childWidth_orig  * scaleX  （min 1）
 *  childHeight'= childHeight_orig * scaleY  （min 1）
 *  line.endX'  = endX_orig * scaleX
 *  line.endY'  = endY_orig * scaleY
 */

import { useRef, useEffect, useState } from 'react';
import type { ElementPatch } from '@/components/canvas/document/publicTypes';
import { applyAspectRatio, type ResizeHandle } from './useResize';

// ── 子元素快照（拖拽开始时采样，拖拽过程中保持不变）─────────────────────────

interface ChildSnapshot {
  id: string;
  type: string;
  x: number;
  y: number;
  width: number;
  height: number;
  endX?: number; // 线条
  endY?: number; // 线条
}

export interface GroupResizeState {
  id: string;
  handle: ResizeHandle;
  startX: number;
  startY: number;
  startW: number;
  startH: number;
  startClientX: number;
  startClientY: number;
  childrenSnapshot: ChildSnapshot[];
}

type Rect = { x: number; y: number; width: number; height: number };

/** 按比例计算子元素的新位置/尺寸 */
function scaleChild(child: ChildSnapshot, scaleX: number, scaleY: number): ElementPatch {
  const patch: ElementPatch = {
    layout: {
      x: child.x * scaleX,
      y: child.y * scaleY,
    },
    size: {
      width: Math.max(1, child.width * scaleX),
      height: Math.max(1, child.height * scaleY),
    },
  };
  if (child.type === 'line') {
    patch.data = {
      end: {
        x: (child.endX ?? child.width) * scaleX,
        y: (child.endY ?? 0) * scaleY,
      },
    };
  }
  return patch;
}

/** 根据 handle 和鼠标偏移计算新的组矩形 */
export function computeGroupRect(
  handle: ResizeHandle,
  startX: number, startY: number, startW: number, startH: number,
  dCanvasX: number, dCanvasY: number
): Rect {
  let x = startX, y = startY, w = startW, h = startH;
  if (handle === 'nw') { w = Math.max(1, startW - dCanvasX); h = Math.max(1, startH - dCanvasY); x = startX + startW - w; y = startY + startH - h; }
  else if (handle === 'n')  { h = Math.max(1, startH - dCanvasY); y = startY + startH - h; }
  else if (handle === 'ne') { w = Math.max(1, startW + dCanvasX); h = Math.max(1, startH - dCanvasY); y = startY + startH - h; }
  else if (handle === 'e')  { w = Math.max(1, startW + dCanvasX); }
  else if (handle === 'se') { w = Math.max(1, startW + dCanvasX); h = Math.max(1, startH + dCanvasY); }
  else if (handle === 's')  { h = Math.max(1, startH + dCanvasY); }
  else if (handle === 'sw') { w = Math.max(1, startW - dCanvasX); h = Math.max(1, startH + dCanvasY); x = startX + startW - w; }
  else if (handle === 'w')  { w = Math.max(1, startW - dCanvasX); x = startX + startW - w; }
  return { x, y, width: w, height: h };
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useGroupResize(
  zoom: number,
  updateElementSilent: (id: string, patch: ElementPatch) => void,
  commitGroupResize: (
    groupId: string,
    groupPatch: { x: number; y: number; width: number; height: number },
    childrenPatches: { id: string; patch: ElementPatch }[]
  ) => void
) {
  const [groupResizeState, setGroupResizeState] = useState<GroupResizeState | null>(null);
  const [groupResizePreview, setGroupResizePreview] = useState<Rect | null>(null);
  const groupResizePreviewRef = useRef<Rect | null>(null);

  useEffect(() => {
    if (!groupResizeState) return;
    const { id, handle, startX, startY, startW, startH, startClientX, startClientY, childrenSnapshot } = groupResizeState;

    const onMove = (e: MouseEvent) => {
      const dX = (e.clientX - startClientX) / zoom;
      const dY = (e.clientY - startClientY) / zoom;
      let newRect = computeGroupRect(handle, startX, startY, startW, startH, dX, dY);
      if (e.shiftKey) {
        const { x, y, w, h } = applyAspectRatio(handle, startW, startH, newRect.x, newRect.y, newRect.width, newRect.height);
        newRect = { x, y, width: w, height: h };
      }
      const scaleX = newRect.width / startW;
      const scaleY = newRect.height / startH;

      // 静默更新（不产生历史）：组容器 + 每个子元素
      updateElementSilent(id, {
        layout: { x: newRect.x, y: newRect.y },
        size: { width: newRect.width, height: newRect.height },
      });
      for (const child of childrenSnapshot) {
        updateElementSilent(child.id, scaleChild(child, scaleX, scaleY));
      }

      groupResizePreviewRef.current = newRect;
      setGroupResizePreview(newRect);
    };

    const onUp = () => {
      const finalRect = groupResizePreviewRef.current;
      if (finalRect) {
        const scaleX = finalRect.width / startW;
        const scaleY = finalRect.height / startH;
        const childrenPatches = childrenSnapshot.map((child) => ({
          id: child.id,
          patch: scaleChild(child, scaleX, scaleY),
        }));
        commitGroupResize(id, finalRect, childrenPatches);
      }
      setGroupResizeState(null);
      setGroupResizePreview(null);
      groupResizePreviewRef.current = null;
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [groupResizeState, zoom, updateElementSilent, commitGroupResize]);

  return {
    groupResizeState,
    setGroupResizeState,
    groupResizePreview,
    setGroupResizePreview,
    groupResizePreviewRef,
  };
}
